"use client";

import Link from "@/components/ui/CcLink";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";

import type {
  FaqCategory,
  FaqListEntry,
  HomeFaqContent,
} from "@/components/home/faq/home-faq-copy";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { splitFaqLinks } from "@/lib/faq-link-parts";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";

/** Answer text with its internal links (`[text](/path)`, see `faq-links.ts`). */
function FaqAnswer({ text }: { text: string }) {
  return splitFaqLinks(text).map((part, index) =>
    part.href ? (
      <Link key={index} href={part.href} className="cc-faq-item__link">
        {part.text}
      </Link>
    ) : (
      part.text
    ),
  );
}

function faqEntryKey(categoryId: string, index: number): string {
  return `${categoryId}-${index}`;
}

function buildCategoryEntries(category: FaqCategory): FaqListEntry[] {
  return category.items.map((item, index) => ({
    key: faqEntryKey(category.id, index),
    item,
  }));
}

function formatFaqIndex(displayIndex: number): string {
  return String(displayIndex + 1).padStart(2, "0");
}

const CC_FAQ_SECTION_TITLE_ID = "cc-faq-section-title";

export type CcFaqTheme = "dark" | "light";
export type CcFaqLayout = "split" | "centered";

type CcFaqClientProps = {
  content: HomeFaqContent;
  theme?: CcFaqTheme;
  layout?: CcFaqLayout;
  /** Dev inspo inspector key (`inspo-sources.ts`). */
  devSectionKey?: string;
};

export function CcFaqClient({
  content,
  theme = "dark",
  layout = "split",
  devSectionKey = "faq",
}: CcFaqClientProps) {
  const { ui, categories } = content;
  const isCentered = layout === "centered";
  const { ref, withRevealClassName } = useCcSectionReveal({ staggerChildren: true });
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [openKey, setOpenKey] = useState<string | null>(() =>
    layout === "split" ? faqEntryKey(categories[0].id, 0) : null,
  );
  const [skipListHeightTransition, setSkipListHeightTransition] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectCategory = useCallback(
    (index: number) => {
      if (index !== activeCategoryIndex) {
        setSkipListHeightTransition(true);
      }
      setActiveCategoryIndex(index);
      if (isCentered) {
        setOpenKey(null);
        return;
      }
      const category = categories[index];
      setOpenKey(faqEntryKey(category.id, 0));
    },
    [activeCategoryIndex, categories, isCentered],
  );

  useLayoutEffect(() => {
    if (!skipListHeightTransition) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      setSkipListHeightTransition(false);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [skipListHeightTransition, activeCategoryIndex]);

  const tabCount = categories.length;
  const activeCategory = categories[activeCategoryIndex];
  const centeredPanelsStyle =
    isCentered && activeCategory
      ? ({ "--cc-faq-item-count": activeCategory.items.length } as CSSProperties)
      : undefined;

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (index + 1) % tabCount;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (index - 1 + tabCount) % tabCount;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabCount - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      selectCategory(nextIndex);
      tabRefs.current[nextIndex]?.focus();
    },
    [selectCategory, tabCount],
  );

  const renderAccordionItems = (
    entries: FaqListEntry[],
    panelPrefix: string,
    itemVariant: "default" | "centered",
  ) =>
    entries.map((entry, index) => {
      const isOpen = openKey === entry.key;
      const panelId = `${panelPrefix}-answer-${entry.key}`;
      const buttonId = `${panelPrefix}-question-${entry.key}`;

      return (
        <div
          key={entry.key}
          className={`cc-faq-item${isOpen ? " is-open" : ""}${
            itemVariant === "centered" ? " cc-faq-item--centered" : ""
          }`}
          style={
            itemVariant === "centered"
              ? ({ "--cc-faq-i": index } as CSSProperties)
              : undefined
          }
        >
          <button
            id={buttonId}
            type="button"
            className="cc-faq-item__question"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => setOpenKey(isOpen ? null : entry.key)}
          >
            {itemVariant === "centered" ? (
              <span className="cc-faq-item__index" aria-hidden="true">
                {formatFaqIndex(index)}
              </span>
            ) : null}
            <span className="cc-faq-item__text">{entry.item.question}</span>
            <span className="cc-faq-item__icon" aria-hidden="true">
              {itemVariant === "centered" ? (
                <>
                  <span className="cc-faq-item__toggle cc-faq-item__toggle--plus">
                    +
                  </span>
                  <span className="cc-faq-item__toggle cc-faq-item__toggle--close">
                    ×
                  </span>
                </>
              ) : (
                <>
                  <span className="cc-faq-item__bar cc-faq-item__bar--h" />
                  <span className="cc-faq-item__bar cc-faq-item__bar--v" />
                </>
              )}
            </span>
          </button>
          <div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            className="cc-faq-item__answer"
            aria-hidden={!isOpen}
            inert={!isOpen}
          >
            <div className="cc-faq-item__answer-inner">
              <div className="cc-faq-item__answer-content">
                <p>
                  <FaqAnswer text={entry.item.answer} />
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    });

  const sectionClassName = withRevealClassName(
    ["cc-faq", isCentered ? "cc-faq--centered" : ""].filter(Boolean).join(" "),
  );

  const tabsToRender = categories.map(({ id, label }) => ({ id, label }));

  return (
    <section
      ref={ref}
      className={sectionClassName}
      id="faq"
      aria-labelledby={CC_FAQ_SECTION_TITLE_ID}
      data-cc-faq-theme={theme}
      data-cc-nav={theme === "light" ? "light" : "dark"}
      {...devSectionAttr(devSectionKey)}
    >
      <div className="cc-faq__frame">
        <div className="cc-faq__container">
          <div className="cc-faq__intro">
            <CcSanjayaEyebrow theme={theme === "light" ? "on-subtle" : "on-dark"}>
              {ui.eyebrowSplit}
            </CcSanjayaEyebrow>
            <h2 id={CC_FAQ_SECTION_TITLE_ID} className="cc-faq__title">
              {isCentered ? ui.titleCentered : ui.titleSplit}
            </h2>
            {isCentered && ui.subtitleCentered ? (
              <p className="cc-faq__subtitle">{ui.subtitleCentered}</p>
            ) : null}
          </div>

          <div className="cc-faq__body">
            <div
              className="cc-faq__tabs"
              role="tablist"
              aria-label={ui.tablistAriaLabel}
            >
              {tabsToRender.map((category, index) => {
                const isActive = index === activeCategoryIndex;

                return (
                  <button
                    key={category.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`cc-faq-tab-${category.id}`}
                    className={`cc-faq__tab${isActive ? " is-active" : ""}`}
                    aria-selected={isActive}
                    aria-controls={`cc-faq-panel-${category.id}`}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectCategory(index)}
                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>

            <div
              className={`cc-faq__panels${
                isCentered ? " cc-faq__panels--centered" : ""
              }`}
              style={centeredPanelsStyle}
              data-cc-faq-skip-list-transition={
                skipListHeightTransition ? "true" : undefined
              }
            >
              {categories.map((category, categoryIndex) => {
                const isPanelActive = categoryIndex === activeCategoryIndex;
                const panelEntries = buildCategoryEntries(category);

                return (
                  <div
                    key={category.id}
                    id={`cc-faq-panel-${category.id}`}
                    role="tabpanel"
                    className={`cc-faq__list${
                      isPanelActive ? "" : " cc-faq__panel--inactive"
                    }`}
                    hidden={!isPanelActive}
                    inert={!isPanelActive ? true : undefined}
                    aria-labelledby={`cc-faq-tab-${category.id}`}
                  >
                    {renderAccordionItems(
                      panelEntries,
                      "cc-faq",
                      isCentered ? "centered" : "default",
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
